import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  type SortingState,
  type RowSelectionState,
  type PaginationState,
} from '@tanstack/react-table';
import { Checkbox } from './Checkbox.js';
import { Button } from './Button.js';
import { LoadingState } from './LoadingState.js';

export type { ColumnDef } from '@tanstack/react-table';

// ── Types ─────────────────────────────────────────────────────────────────────

import type { ColumnDef } from '@tanstack/react-table';

export type DataTableSize    = 'sm' | 'md';
export type DataTableDensity = 'compact' | 'default' | 'comfortable';

export interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  size?: DataTableSize;
  density?: DataTableDensity;
  selectable?: boolean;
  onSelectionChange?: (rows: T[]) => void;
  sortable?: boolean;
  pagination?: { pageSize: number } | false;
  emptyMessage?: string;
  loading?: boolean;
  getRowId?: (row: T) => string;
}

// ── Sizing ────────────────────────────────────────────────────────────────────

const ROW_FS:  Record<DataTableSize, string>    = { sm: '12px', md: '13px' };
const V_PAD:   Record<DataTableDensity, string> = { compact: '6px', default: '10px', comfortable: '14px' };

// ── Sort icon ─────────────────────────────────────────────────────────────────

function SortIcon({ dir }: { dir: 'asc' | 'desc' | false }) {
  const color = dir ? 'var(--evo-cyan)' : 'var(--evo-fg-4)';
  return (
    <svg width={10} height={10} viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0, color }}>
      <path
        d="M5 2L2 5H8L5 2Z"
        fill={dir === 'asc' ? 'currentColor' : 'var(--evo-fg-4)'}
      />
      <path
        d="M5 8L8 5H2L5 8Z"
        fill={dir === 'desc' ? 'currentColor' : 'var(--evo-fg-4)'}
      />
    </svg>
  );
}

// ── DataTable ─────────────────────────────────────────────────────────────────

export function DataTable<T>({
  columns,
  data,
  size = 'md',
  density = 'default',
  selectable = false,
  onSelectionChange,
  sortable = true,
  pagination = false,
  emptyMessage,
  loading = false,
  getRowId,
}: DataTableProps<T>) {
  const { t } = useTranslation();
  const resolvedEmptyMessage = emptyMessage ?? t('common.noResults');
  const [sorting, setSorting]               = useState<SortingState>([]);
  const [rowSelection, setRowSelection]     = useState<RowSelectionState>({});
  const [paginationState, setPaginationState] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pagination ? pagination.pageSize : 1_000_000,
  });

  // Build selection column if needed
  const selectionCol: ColumnDef<T> = {
    id: '__select__',
    size: 36,
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected()}
        indeterminate={table.getIsSomePageRowsSelected()}
        onChange={table.toggleAllPageRowsSelected}
        size="sm"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={row.toggleSelected}
        size="sm"
      />
    ),
    enableSorting: false,
  };

  const allCols: ColumnDef<T>[] = selectable ? [selectionCol, ...columns] : columns;

  const table = useReactTable<T>({
    data,
    columns: allCols,
    state: {
      sorting,
      rowSelection,
      pagination: paginationState,
    },
    onSortingChange: setSorting,
    onRowSelectionChange: (updater) => {
      setRowSelection(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (onSelectionChange) {
          const selectedRows = table.getSelectedRowModel().rows.map(r => r.original);
          setTimeout(() => onSelectionChange(selectedRows), 0);
        }
        return next;
      });
    },
    onPaginationChange: setPaginationState,
    getCoreRowModel: getCoreRowModel(),
    ...(sortable   ? { getSortedRowModel: getSortedRowModel() }       : {}),
    ...(pagination ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    ...(getRowId   ? { getRowId: (row: T) => getRowId(row) }           : {}),
    enableRowSelection: selectable,
  });

  const headerGroups = table.getHeaderGroups();
  const rows         = table.getRowModel().rows;
  const colCount     = allCols.length;
  const H_PAD        = '12px';
  const vPad         = V_PAD[density];
  const fs           = ROW_FS[size];

  // Pagination info
  const pageCount  = table.getPageCount();
  const pageIndex  = table.getState().pagination.pageIndex;
  const pageSize   = table.getState().pagination.pageSize;
  const totalRows  = data.length;
  const rowStart   = pageIndex * pageSize + 1;
  const rowEnd     = Math.min((pageIndex + 1) * pageSize, totalRows);

  return (
    <div style={{ position: 'relative', border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--evo-font)' }}>
          <thead style={{ background: 'var(--evo-bg-3)', borderBottom: '1px solid var(--evo-stroke)' }}>
            {headerGroups.map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => {
                  const canSort    = sortable && header.column.getCanSort();
                  const sortDir    = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      style={{
                        padding: `10px ${H_PAD}`,
                        fontSize: '11px',
                        fontWeight: 500,
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        color: canSort && sortDir ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)',
                        textAlign: 'left',
                        cursor: canSort ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                        width: header.column.columnDef.size,
                        userSelect: 'none',
                        transition: 'color 80ms',
                      }}
                      onMouseEnter={e => { if (canSort) (e.currentTarget as HTMLElement).style.color = 'var(--evo-fg-1)'; }}
                      onMouseLeave={e => { if (canSort && !sortDir) (e.currentTarget as HTMLElement).style.color = 'var(--evo-fg-3)'; }}
                    >
                      {header.isPlaceholder ? null : (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon dir={sortDir === false ? false : sortDir} />}
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading && data.length === 0 ? null : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={colCount}
                  style={{
                    padding: '32px 12px', textAlign: 'center',
                    fontSize: '13px', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)',
                  }}
                >
                  {resolvedEmptyMessage}
                </td>
              </tr>
            ) : rows.map((row, idx) => {
              const isSelected = row.getIsSelected();
              const isLast     = idx === rows.length - 1;
              return (
                <tr
                  key={row.id}
                  style={{
                    borderBottom: isLast ? 'none' : '1px solid var(--evo-stroke)',
                    background: isSelected ? 'rgba(0,250,200,0.06)' : 'transparent',
                    borderLeft: isSelected ? '2px solid var(--evo-cyan)' : '2px solid transparent',
                    transition: 'background 80ms',
                  }}
                  onMouseEnter={e => { if (!isSelected) (e.currentTarget as HTMLElement).style.background = 'var(--evo-bg-3)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = isSelected ? 'rgba(0,250,200,0.06)' : 'transparent'; }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      style={{
                        padding: `${vPad} ${H_PAD}`,
                        fontSize: fs,
                        color: 'var(--evo-fg-2)',
                        fontFamily: 'var(--evo-font)',
                        verticalAlign: 'middle',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination footer */}
      {pagination !== false && totalRows > 0 && (
        <div style={{
          borderTop: '1px solid var(--evo-stroke)',
          padding: '8px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '12px',
          color: 'var(--evo-fg-3)',
          fontFamily: 'var(--evo-font)',
        }}>
          <span>Showing {rowStart}–{rowEnd} of {totalRows}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>Page {pageIndex + 1} of {pageCount}</span>
            <Button
              variant="ghost" tone="muted" size="sm"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              Prev
            </Button>
            <Button
              variant="ghost" tone="muted" size="sm"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'rgba(10,10,10,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10,
        }}>
          <LoadingState fullHeight />
        </div>
      )}
    </div>
  );
}
